/**
 * @file MissingExpiryConfirm.tsx
 * @description Подтверждение проведения приёмки с предупреждениями
 * (phase19.22, W20, W17-9b; ui-design §7): «3 позиции без срока годности —
 * партии будут помечены „срок не указан“ и уйдут в продажу первыми.
 * [Провести без срока] [Заполнить сроки]».
 *
 * Основная кнопка — «Заполнить сроки»: срок проще вписать сейчас, чем потом
 * «Указать срок» и переклеивать этикетки. Но выбор за кладовщиком — срок
 * «мягко» обязателен и проведение не блокирует.
 */

import { CalendarClock, Loader2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import type { ReceiptWarning } from "../../lib/api-wms";
import { missingExpiryTitle } from "../../lib/receipt-expiry";

interface Props {
  warnings: ReceiptWarning[] | null;
  committing: boolean;
  /** «Провести без срока». */
  onConfirm: () => void;
  /** «Заполнить сроки» — к первой строке без срока. */
  onFill: (itemIds: string[]) => void;
  onCancel: () => void;
}

export function MissingExpiryConfirm({ warnings, committing, onConfirm, onFill, onCancel }: Props) {
  const expiry = warnings?.find((w) => w.code === "expiry_missing");
  const others = (warnings ?? []).filter((w) => w.code !== "expiry_missing");
  const count = expiry?.itemIds.length ?? 0;

  return (
    <AlertDialog open={!!warnings} onOpenChange={(v) => (!v ? onCancel() : null)}>
      <AlertDialogContent fullScreen className="gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <CalendarClock className="size-5 shrink-0 text-warning" aria-hidden />
            {count > 0 ? missingExpiryTitle(count) : "Проверьте приёмку перед проведением"}
          </AlertDialogTitle>
          {count > 0 ? (
            <AlertDialogDescription>
              Партии будут помечены «срок не указан» и уйдут в продажу первыми. На
              этикетке срок будет «—». Указать срок можно и после проведения, но
              этикетки тогда придётся перепечатать.
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        {others.map((w) => (
          <p key={w.code} className="rounded-lg bg-warning/12 p-3 text-sm">
            {w.message}
          </p>
        ))}
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={onConfirm}
            disabled={committing}
            className="min-h-14 w-full sm:w-auto"
          >
            {committing ? <Loader2 className="size-4 animate-spin" /> : null}
            <span className={committing ? "ml-2" : ""}>
              {count > 0 ? "Провести без срока" : "Провести всё равно"}
            </span>
          </Button>
          <Button
            onClick={() => (count > 0 ? onFill(expiry?.itemIds ?? []) : onCancel())}
            disabled={committing}
            className="min-h-14 w-full sm:w-auto"
          >
            {count > 0 ? "Заполнить сроки" : "Вернуться к документу"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
